import config from "@/config";
import { apiRequest } from "@/lib/api";

export type EnrollmentListRow = {
  orderId: string;
  orderNumber: string | null;
  enrolledAt: string;
  enrollmentDate: string | null;
  orderCreatedAt: string;
  program: string;
  programName: string | null;
  deliveryMode: string;
  startDate: string | null;
  startMonth: string | null;
  batch: string | null;
  orderStatus: string;
  programStatus: string;
  paymentStatus: string | null;
  paymentType: string | null;
  paymentAmount: number | null;
  totalAmount: number | null;
  totalPaid: number | null;
  totalDue: number | null;
  student: {
    _id: string;
    accountId: string | null;
    email: string;
    fullName: string | null;
    cellPhoneNumber: string | null;
    isBlocked: boolean;
    isDeleted: boolean;
  };
  subscription: {
    completedPayments: number | null;
    numberOfPayments: number | null;
    status: string | null;
  } | null;
};

export type EnrollmentListQuery = {
  page?: number;
  perPage?: number;
  search?: string;
  program?: string;
  /** Comma-separated statuses, or `all` for every status */
  orderStatus?: string;
  paymentStatus?: "PENDING" | "COMPLETED" | "FAILED" | "REFUNDED";
  paymentType?: "ONE_TIME" | "SUBSCRIPTION";
  enrolledFrom?: string;
  enrolledTo?: string;
  activeStudentsOnly?: boolean;
  sortBy?: "enrolledAt" | "program" | "orderStatus" | "paymentAmount" | "createdAt";
  sortOrder?: "asc" | "desc";
};

export type EnrollmentListResponse = {
  success: boolean;
  message: string;
  data: {
    enrollments: EnrollmentListRow[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      pages: number;
    };
    summary: {
      enrolledToday: number;
      enrolledThisWeek: number;
      totalMatching: number;
    };
  };
};

function buildQueryString(params: EnrollmentListQuery): string {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.perPage != null) qs.set("perPage", String(params.perPage));
  if (params.search?.trim()) qs.set("search", params.search.trim());
  if (params.program?.trim()) qs.set("program", params.program.trim());
  if (params.orderStatus) qs.set("orderStatus", params.orderStatus);
  if (params.paymentStatus) qs.set("paymentStatus", params.paymentStatus);
  if (params.paymentType) qs.set("paymentType", params.paymentType);
  if (params.enrolledFrom) qs.set("enrolledFrom", params.enrolledFrom);
  if (params.enrolledTo) qs.set("enrolledTo", params.enrolledTo);
  if (params.activeStudentsOnly === false) {
    qs.set("activeStudentsOnly", "false");
  } else if (params.activeStudentsOnly === true) {
    qs.set("activeStudentsOnly", "true");
  }
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortOrder) qs.set("sortOrder", params.sortOrder);
  return qs.toString();
}

export const enrollmentsApi = {
  list: async (params: EnrollmentListQuery = {}) => {
    const qs = buildQueryString(params);
    return apiRequest<EnrollmentListResponse>(
      `/api/admin/enrollments${qs ? `?${qs}` : ""}`
    );
  },

  exportCsv: async (params: EnrollmentListQuery = {}): Promise<Blob> => {
    const qs = buildQueryString(params);
    const base = config.apiUrl.replace(/\/$/, "");
    const response = await fetch(
      `${base}/api/admin/enrollments/export${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Export failed (${response.status})`);
    }
    return response.blob();
  },

  pause: async (orderId: string, data?: { reason?: string }) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: {
        orderId: string;
        programStatus: string;
        programName: string | null;
        subscriptionId: string | null;
        billingAction: string;
        lmsRevoked: boolean;
        lmsRestored: boolean;
      };
    }>(`/api/admin/enrollments/${orderId}/pause`, {
      method: "POST",
      body: data ?? {},
    });
  },

  resume: async (orderId: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: {
        orderId: string;
        programStatus: string;
        programName: string | null;
        subscriptionId: string | null;
        billingAction: string;
        lmsRevoked: boolean;
        lmsRestored: boolean;
      };
    }>(`/api/admin/enrollments/${orderId}/resume`, {
      method: "POST",
      body: {},
    });
  },

  drop: async (orderId: string, data?: { reason?: string }) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: {
        orderId: string;
        programStatus: string;
        programName: string | null;
        subscriptionId: string | null;
        billingAction: string;
        lmsRevoked: boolean;
        lmsRestored: boolean;
      };
    }>(`/api/admin/enrollments/${orderId}/drop`, {
      method: "POST",
      body: data ?? {},
    });
  },
};

/** Default successful order statuses for the enrollments table. */
export const DEFAULT_ENROLLMENT_ORDER_STATUS =
  "PAYMENT_COMPLETED,ENROLLED,COMPLETED";

export const ALL_ENROLLMENT_ORDER_STATUS =
  "PENDING,PENDING_PAYMENT,PAYMENT_COMPLETED,ENROLLED,COMPLETED,CANCELLED,REFUNDED";
